import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Camera, X, SwitchCamera, Loader2 } from "lucide-react";

export default function BarcodeScanner({ onScan, onClose }) {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [isStarting, setIsStarting] = useState(true);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);

  useEffect(() => {
    let mounted = true;

    const startScanner = async () => {
      try {
        // Get available cameras
        const devices = await Html5Qrcode.getCameras();
        if (!mounted) return;

        if (devices && devices.length > 0) {
          setCameras(devices);
          
          // Prefer back camera if available
          const backCameraIndex = devices.findIndex(
            (d) => d.label.toLowerCase().includes("back") || 
                   d.label.toLowerCase().includes("rear") ||
                   d.label.toLowerCase().includes("environment")
          );
          const startIndex = backCameraIndex >= 0 ? backCameraIndex : 0;
          setCurrentCameraIndex(startIndex);

          // Create scanner instance
          html5QrCodeRef.current = new Html5Qrcode("barcode-scanner-region");
          
          await html5QrCodeRef.current.start(
            devices[startIndex].id,
            {
              fps: 10,
              qrbox: { width: 250, height: 150 },
              aspectRatio: 1.5,
            },
            (decodedText) => {
              // Success callback
              if (mounted) {
                onScan(decodedText);
                stopScanner();
              }
            },
            () => {
              // Ignore errors during scanning (expected when no barcode in view)
            }
          );

          if (mounted) {
            setIsStarting(false);
          }
        } else {
          setError("No cameras found on this device");
          setIsStarting(false);
        }
      } catch (err) {
        console.error("Scanner error:", err);
        if (mounted) {
          if (err.name === "NotAllowedError") {
            setError("Camera access denied. Please allow camera permissions.");
          } else if (err.name === "NotFoundError") {
            setError("No camera found on this device.");
          } else {
            setError("Failed to start camera. Please try again.");
          }
          setIsStarting(false);
        }
      }
    };

    startScanner();

    return () => {
      mounted = false;
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING state
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
      html5QrCodeRef.current = null;
    }
  };

  const switchCamera = async () => {
    if (cameras.length <= 1) return;
    
    const nextIndex = (currentCameraIndex + 1) % cameras.length;
    setCurrentCameraIndex(nextIndex);
    setIsStarting(true);
    
    try {
      await stopScanner();
      
      html5QrCodeRef.current = new Html5Qrcode("barcode-scanner-region");
      
      await html5QrCodeRef.current.start(
        cameras[nextIndex].id,
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.5,
        },
        (decodedText) => {
          onScan(decodedText);
          stopScanner();
        },
        () => {}
      );
      
      setIsStarting(false);
    } catch (err) {
      console.error("Error switching camera:", err);
      setError("Failed to switch camera");
      setIsStarting(false);
    }
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <Card className="relative overflow-hidden rounded-2xl border-2 border-primary/20">
      <CardContent className="p-0">
        {/* Scanner Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-3 bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center gap-2 text-white">
            <Camera className="w-4 h-4" />
            <span className="text-sm font-medium">Scan Barcode</span>
          </div>
          <div className="flex items-center gap-2">
            {cameras.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={switchCamera}
                className="h-8 w-8 text-white hover:bg-white/20"
                data-testid="switch-camera-btn"
              >
                <SwitchCamera className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="h-8 w-8 text-white hover:bg-white/20"
              data-testid="close-scanner-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Scanner Region */}
        <div 
          id="barcode-scanner-region" 
          ref={scannerRef}
          className="min-h-[300px] bg-black"
          style={{ width: "100%" }}
        />

        {/* Loading State */}
        {isStarting && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center text-white">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p className="text-sm">Starting camera...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90">
            <div className="text-center text-white p-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                <X className="w-6 h-6 text-red-400" />
              </div>
              <p className="text-sm mb-4">{error}</p>
              <Button variant="outline" size="sm" onClick={handleClose}>
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Scanner Overlay Guide */}
        {!isStarting && !error && (
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
            <p className="text-white text-center text-xs">
              Position the barcode within the frame
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
